import { useCallback, useState } from 'react';
import { useTheme } from '@mui/material';
import DSBox from '../../../design-system/DSBox';
import DSPaper from '../../../design-system/Paper/DSPaper';
import DSTooltip from '../../../design-system/DSTooltip';
import DSIconButton from '../../../design-system/DSIconButton';
import DSHelpOutline from '../../../design-system/DSHelpOutline';
import DSSecondaryButton from '../../../design-system/DSSecondaryButton';
import { DSTypography } from '../../../design-system/Typography';
import KlydoThumbnailPickerModal from './KlydoThumbnailPickerModal';
import { THUMBNAIL_FRAME_SPEC } from '../../../consts/specs';
import {
  formatFrameOffset,
  getKlydoStillFrameUrl,
  supportsFrameSelection,
} from '../../../utils/klydoThumbnail';

const COPY = {
  label: 'Thumbnail',
  autoFrame: 'Automatically selected frame',
  hint: 'Shown wherever your Klydo is not animating',
  choose: 'Choose frame',
  reset: 'Reset',
};

type KlydoThumbnailInputProps = {
  loopUrl: string;
  offsetSeconds: number | null;
  durationInSeconds?: number;
  onOffsetChange: (offsetSeconds: number | null) => void;
};

const KlydoThumbnailInput = ({
  loopUrl,
  offsetSeconds,
  durationInSeconds,
  onOffsetChange,
}: KlydoThumbnailInputProps) => {
  const { spacing, palette } = useTheme();
  const [isPickerOpen, setIsPickerOpen] = useState(false);

  const handleOpenPicker = useCallback(() => setIsPickerOpen(true), []);
  const handleClosePicker = useCallback(() => setIsPickerOpen(false), []);

  const handleConfirmFrame = useCallback(
    (nextOffsetSeconds: number) => {
      onOffsetChange(nextOffsetSeconds);
      setIsPickerOpen(false);
    },
    [onOffsetChange],
  );

  const handleResetToAuto = useCallback(() => {
    onOffsetChange(null);
  }, [onOffsetChange]);

  if (!supportsFrameSelection(loopUrl)) return null;

  return (
    <DSBox sx={{ display: 'flex', flexDirection: 'column', gap: spacing(1) }}>
      <DSBox
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
        }}
      >
        <DSTypography variant="label">{COPY.label}</DSTypography>
        <DSTooltip title={THUMBNAIL_FRAME_SPEC}>
          <DSIconButton
            sx={{
              height: spacing(1),
              '&:hover': {
                backgroundColor: 'transparent',
                color: palette.text.primary,
              },
              color: palette.text.secondary,
              transition: 'color 0.3s ease',
            }}
          >
            <DSHelpOutline />
          </DSIconButton>
        </DSTooltip>
      </DSBox>

      <DSPaper
        sx={{
          display: 'flex',
          height: spacing(15),
          padding: spacing(1, 4),
          justifyContent: 'space-between',
          alignItems: 'center',
          alignSelf: 'stretch',
          borderRadius: spacing(3),
          backgroundColor: palette.white[5],
        }}
      >
        <DSBox
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: spacing(2),
            minWidth: 0,
          }}
        >
          <DSBox
            sx={{
              width: spacing(9),
              height: spacing(9),
              borderRadius: '50%',
              overflow: 'hidden',
              flexShrink: 0,
              backgroundColor: palette.white[8],
            }}
          >
            <img
              src={getKlydoStillFrameUrl({ loopUrl, offsetSeconds })}
              alt=""
              draggable={false}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block',
              }}
            />
          </DSBox>
          <DSBox sx={{ display: 'flex', flexDirection: 'column' }}>
            <DSTypography sx={{ color: palette.text.primary }}>
              {offsetSeconds === null
                ? COPY.autoFrame
                : `Frame at ${formatFrameOffset(offsetSeconds)}`}
            </DSTypography>
            <DSTypography variant="body2" color={palette.text.secondary}>
              {COPY.hint}
            </DSTypography>
          </DSBox>
        </DSBox>

        <DSBox
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: spacing(1),
            flexShrink: 0,
          }}
        >
          {offsetSeconds !== null && (
            <DSSecondaryButton size="small" onClick={handleResetToAuto}>
              {COPY.reset}
            </DSSecondaryButton>
          )}
          <DSSecondaryButton
            variant="contained"
            size="small"
            onClick={handleOpenPicker}
          >
            {COPY.choose}
          </DSSecondaryButton>
        </DSBox>
      </DSPaper>

      <KlydoThumbnailPickerModal
        open={isPickerOpen}
        onClose={handleClosePicker}
        onConfirm={handleConfirmFrame}
        loopUrl={loopUrl}
        offsetSeconds={offsetSeconds}
        durationInSeconds={durationInSeconds}
      />
    </DSBox>
  );
};

export default KlydoThumbnailInput;
